import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import { Title } from "components/Common/Title"
import { ExamSectionSkeleton } from "./ExamSectionSkeleton"
import { hiabColors } from "theme"
import analyticsService from "services/analyticsService"
import type { IAnalyticsExamsLesson } from "types/analyticsTypes"
import { applyPassRateFilter } from "./analyticsFilters"
import type { PassRateFilterOption } from "./analyticsFilters"

type FilterOption = PassRateFilterOption

type Props = {
  orgId: string | null
}

export function ExamLessonsSection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamsLesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterOption>("bottom10")

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)
    analyticsService
      .getExamsLessons(orgId)
      .then((result) => {
        const sorted = [...result].sort((a, b) => a.passRate - b.passRate)
        setData(sorted)
        setLoading(false)
      })
      .catch(() => {
        setError("Failed to load lesson data.")
        setLoading(false)
      })
  }, [orgId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFilterChange = (event: SelectChangeEvent<FilterOption>) => {
    setFilter(event.target.value as FilterOption)
  }

  if (loading) return <ExamSectionSkeleton rows={5} />

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>PASS RATES BY LESSON</Title>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
        <Button size="small" onClick={fetchData} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Paper>
    )
  }

  const displayData = applyPassRateFilter(data, filter)

  return (
    <Paper sx={{ p: 3 }}>
      <Title>PASS RATES BY LESSON</Title>

      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No exam lesson data available.
        </Typography>
      ) : (
        <>
          <Box sx={{ mb: 1, mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Show</InputLabel>
              <Select<FilterOption> value={filter} label="Show" onChange={handleFilterChange}>
                <MenuItem value="bottom10">Bottom 10</MenuItem>
                <MenuItem value="top10">Top 10</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Lesson</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Exams Taken</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Passed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Pass Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((lesson) => (
                  <TableRow key={lesson.lessonId}>
                    <TableCell>{lesson.lessonName ?? `Unknown (${lesson.lessonId})`}</TableCell>
                    <TableCell align="right">{lesson.examsTaken}</TableCell>
                    <TableCell align="right">{lesson.examsPassed}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: lesson.passRate < 50 ? hiabColors.redPrimary : "inherit" }}
                    >
                      {lesson.passRate}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  )
}
